import React from 'react'
import { cn } from '@/lib/utils'

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual variant for common UI patterns */
  variant?: 'text' | 'circular' | 'rectangular'
  /** Width — accepts any CSS value (e.g. "100%", "200px") */
  width?: string
  /** Height — accepts any CSS value */
  height?: string
}

/**
 * Animated placeholder shown while content is loading.
 * Matches the app's design tokens (bg-muted, rounded-md).
 */
const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, variant = 'rectangular', width, height, style, ...props }, ref) => {
    const variantClasses: Record<string, string> = {
      text: 'rounded-md h-4',
      circular: 'rounded-full',
      rectangular: 'rounded-lg',
    }

    return (
      <div
        ref={ref}
        className={cn(
          'skeleton-shimmer bg-muted/70 dark:bg-white/[0.06]',
          variantClasses[variant],
          className,
        )}
        style={{ width, height, ...style }}
        {...props}
      />
    )
  },
)
Skeleton.displayName = 'Skeleton'

/** Skeleton matching a stat card on the dashboard (icon + value + label). */
const StatCardSkeleton: React.FC = () => (
  <div className="p-4 rounded-lg border border-border/50 bg-card">
    <div className="flex items-start justify-between">
      <div className="space-y-2.5 flex-1">
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="text" width="60%" height="28px" />
        <Skeleton variant="text" width="30%" />
      </div>
      <Skeleton variant="circular" width="36px" height="36px" className="opacity-60" />
    </div>
  </div>
)

/** Skeleton for a material card in the grid view. */
const MaterialCardSkeleton: React.FC = () => (
  <div className="rounded-lg border border-border/50 bg-card p-4 space-y-4">
    <div className="flex items-start gap-3">
      <Skeleton variant="circular" width="36px" height="36px" />
      <div className="flex-1 space-y-2">
        <Skeleton variant="text" width="70%" />
        <Skeleton variant="text" width="40%" />
      </div>
    </div>
    <Skeleton variant="rectangular" height="60px" width="100%" />
    <div className="flex items-center justify-between">
      <Skeleton variant="text" width="80px" />
      <Skeleton variant="text" width="50px" />
    </div>
  </div>
)

/** Full dashboard loading skeleton — replaces the spinning loader. */
const DashboardSkeleton: React.FC = () => (
  <div className="space-y-5 animate-fade-in">
    {/* Header */}
    <div className="space-y-2">
      <Skeleton variant="text" width="250px" height="24px" />
      <Skeleton variant="text" width="350px" />
    </div>

    {/* Stats grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <StatCardSkeleton key={i} />
      ))}
    </div>

    {/* Email performance */}
    <div className="rounded-lg border border-border/50 bg-card p-4 space-y-4">
      <Skeleton variant="text" width="180px" height="22px" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="p-3 rounded-lg border-l-2 border-muted bg-muted/20 space-y-2">
            <Skeleton variant="text" width="50%" />
            <Skeleton variant="text" width="70%" height="24px" />
          </div>
        ))}
      </div>
    </div>

    {/* Two-column section */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="rounded-lg border border-border/50 bg-card p-4 space-y-4">
        <Skeleton variant="text" width="150px" height="22px" />
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton variant="text" width="80px" />
            <Skeleton variant="rectangular" height="8px" className="flex-1 rounded-full" />
            <Skeleton variant="text" width="40px" />
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-border/50 bg-card p-4 space-y-4">
        <Skeleton variant="text" width="180px" height="22px" />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rectangular" height="60px" width="100%" />
        ))}
      </div>
    </div>
  </div>
)

export { Skeleton, StatCardSkeleton, MaterialCardSkeleton, DashboardSkeleton }
